import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import useDocumentSettingsFactory from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Sections/StagesSection/Hooks/useDocumentSettingsFactory';
import useWorkflowDraft from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Providers/WorkflowDraftProvider/useWorkflowDraft';
import { css } from '@emotion/react';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

const optionsStyle = css`
	display: flex;
	flex-wrap: wrap;
	gap: 0.375rem;
`;

const emptyStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	color: ${theme.colors.inkMuted};
`;

type Props = {
	/** The stage receiving the document in parallel; null appends a new stage holding it. */
	readonly stageIndex: number | null;
};

/**
 * Opens the list of document types the group does not produce yet, then adds the picked one — in
 * parallel within `stageIndex`, or alone in a new stage after the last one. A document type produces
 * at most one document per group, hence the shrinking list.
 */
const AddDocumentControl: React.FC<Props> = ({ stageIndex }) => {
	const { draft, setDraft } = useWorkflowDraft();
	const documentTypes = useSelector((state) => state.documentTypesStore.documentTypes);
	const createDocumentSettings = useDocumentSettingsFactory();
	const [isOpen, setIsOpen] = React.useState(false);

	const availableTypes = React.useMemo(() => {
		const usedTypeIds = draft.documentTypeIds;

		return (documentTypes?.activeTypes() ?? []).filter(
			(documentType) => !usedTypeIds.includes(documentType.id),
		);
	}, [documentTypes, draft.documentTypeIds]);

	const handleToggleClick = React.useCallback(() => {
		setIsOpen((current) => !current);
	}, []);

	const handlePickClick = React.useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			const documentTypeId = Number(event.currentTarget.value);
			const document = createDocumentSettings(documentTypeId);

			setDraft((current) =>
				stageIndex === null ? current.addStage(document) : current.addDocument(stageIndex, document),
			);
			setIsOpen(false);
		},
		[createDocumentSettings, setDraft, stageIndex],
	);

	const isNewStage = stageIndex === null;
	const addLabel = isNewStage
		? 'Ajouter une étape (à la suite)'
		: '＋ Ajouter un document à cette étape (en parallèle)';

	return (
		<React.Fragment>
			<Button
				type="button"
				size="sm"
				severity="secondary"
				outlined={!isNewStage}
				label={isOpen ? 'Fermer' : addLabel}
				icon={isNewStage && !isOpen ? <Plus size={16} /> : undefined}
				aria-expanded={isOpen}
				onClick={handleToggleClick}
			/>
			<ConditionalLoader data={isOpen}>
				<ConditionalLoader
					data={availableTypes.length > 0}
					fallback={<p css={emptyStyle}>Tous les types de document sont déjà utilisés.</p>}
				>
					<div css={optionsStyle}>
						{availableTypes.map((documentType) => (
							<Button
								key={documentType.id}
								type="button"
								size="sm"
								severity="secondary"
								outlined
								label={`＋ ${documentType.label}`}
								value={String(documentType.id)}
								onClick={handlePickClick}
							/>
						))}
					</div>
				</ConditionalLoader>
			</ConditionalLoader>
		</React.Fragment>
	);
};

export default AddDocumentControl;
